import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import ConfirmModal from "@/components/ConfirmModal";
import { PaRequestOrigin, PaRequestAssigned, AssignReason, defaultPaChoice } from "@/components/admin/PaAssignment";

// §Micro9b.3 + §BLOCCO2 · Richieste di contatto PA (AGISCI).
// Legge GET /api/v2/admin/pa-requests (JWT admin) e consente l'ASSEGNAZIONE
// manuale di una richiesta a un Property Advisor attivo (nessuna
// assegnazione automatica) e la revoca dell'accesso.
function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("it-IT"); } catch { return String(v); }
}

export default function AdminPaRequests() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pas, setPas] = useState([]);
  const [choice, setChoice] = useState({});
  const [reasons, setReasons] = useState([]);
  const [reasonSel, setReasonSel] = useState({});
  const chosenFor = (it) => choice[it.request_id] ?? defaultPaChoice(it, pas);
  const [busy, setBusy] = useState(null);
  const [roleEmail, setRoleEmail] = useState("");
  const [roleVal, setRoleVal] = useState("pa");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const allSelected = items.length > 0 && items.every((it) => selectedIds.has(it.request_id));
  const toggleOne = (id) => setSelectedIds((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((it) => it.request_id)));

  async function confirmDeleteOne() {
    if (!pendingDelete) return;
    setDelBusy(true);
    try {
      await api.delete(`/v2/admin/pa-requests/${pendingDelete.request_id}`);
      toast.success("Richiesta eliminata");
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(pendingDelete.request_id); return n; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error_code || "Eliminazione non riuscita");
    } finally { setDelBusy(false); setPendingDelete(null); }
  }

  async function confirmBulkDelete() {
    setDelBusy(true);
    try {
      const r = await api.post("/v2/admin/pa-requests/bulk-delete", { request_ids: Array.from(selectedIds) });
      toast.success(`Eliminate ${r.data.deleted_count} richieste`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error_code || "Eliminazione in blocco non riuscita");
    } finally { setDelBusy(false); setBulkConfirm(false); }
  }

  async function enablePa() {
    if (!roleEmail.trim()) { toast.error("Indica l'email dell'utente"); return; }
    setBusy("role");
    try {
      await api.post("/v2/admin/users/set-role", { email: roleEmail.trim(), ruolo: roleVal });
      toast.success("Ruolo assegnato");
      setRoleEmail("");
      api.get("/v2/admin/active-pas").then((r) => setPas(r.data.items || [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Assegnazione ruolo non riuscita");
    } finally { setBusy(null); }
  }

  function load() {
    setLoading(true);
    api.get("/v2/admin/pa-requests", { params: { limit: 200, offset: 0 } })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error("Errore caricamento richieste di contatto PA"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    api.get("/v2/admin/active-pas")
      .then((r) => { setPas(r.data.items || []); setReasons(r.data.assignment_reasons || []); })
      .catch(() => {});
  }, []);

  function paLabel(p) {
    const n = [p.nome, p.cognome].filter(Boolean).join(" ").trim();
    return `${n || p.email || p.id} · ${p.ruolo}`;
  }

  async function assign(it) {
    const pa_id = chosenFor(it);
    if (!pa_id) { toast.error("Seleziona un Property Advisor"); return; }
    const overriding = !!it.referral_pa_id && pa_id !== it.referral_pa_id;
    const rs = overriding ? (reasonSel[it.request_id] || {}) : {};
    setBusy(it.request_id);
    try {
      await api.post(`/v2/admin/pa-requests/${it.request_id}/assign`, { pa_id, reason: rs.reason || null, note: rs.note || null });
      toast.success("Richiesta assegnata");
      setReasonSel((prev) => { const n = { ...prev }; delete n[it.request_id]; return n; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Assegnazione non riuscita");
    } finally { setBusy(null); }
  }

  async function revokeAccess(it) {
    setBusy(it.request_id);
    try {
      await api.post(`/v2/admin/pa-requests/${it.request_id}/revoke-access`, {});
      toast.success("Accesso del PA revocato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Revoca non riuscita");
    } finally { setBusy(null); }
  }

  return (
    <div data-testid="admin-pa-requests">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Richieste di contatto PA</h1>
          <p className="text-[13px] text-[#4B5563] mt-1">
            Richieste inviate dal Piano d'Azione (AGISCI) · {total} totali · assegnazione manuale
          </p>
        </div>
        <div className="flex items-center gap-3" data-testid="pa-requests-bulk-toolbar">
          <span className="text-[13px] text-[#4B5563]" data-testid="pa-requests-selected-count">
            {selectedIds.size} selezionate
          </span>
          <button
            type="button"
            onClick={() => setBulkConfirm(true)}
            disabled={selectedIds.size === 0}
            className="inline-flex items-center gap-2 text-[12px] px-3 py-2 rounded-md bg-[#B40101] text-white disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="pa-requests-bulk-delete-btn"
          >
            <Trash2 size={14} /> Elimina selezionate
          </button>
        </div>
      </div>

      <div className="mb-4 bg-white rounded-xl border border-[#E5E5E5] p-4" data-testid="admin-enable-pa">
        <p className="text-[13px] font-semibold text-[#0A0A0A] mb-2">Abilita Property Advisor</p>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            data-testid="enable-pa-email"
            value={roleEmail}
            onChange={(e) => setRoleEmail(e.target.value)}
            placeholder="email utente registrato"
            className="border border-[#D1D5DB] rounded-md px-2 py-1 text-[12px] w-72"
          />
          <select
            data-testid="enable-pa-role"
            value={roleVal}
            onChange={(e) => setRoleVal(e.target.value)}
            className="border border-[#D1D5DB] rounded-md px-2 py-1 text-[12px]"
          >
            <option value="pa">pa</option>
            <option value="fpa">fpa (fondatore)</option>
          </select>
          <button
            type="button"
            data-testid="enable-pa-btn"
            onClick={enablePa}
            disabled={busy === "role"}
            className="text-[12px] px-3 py-1 rounded-md bg-[#0A0A0A] text-white disabled:opacity-50"
          >Assegna ruolo</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="pa-requests-table">
          <thead className="bg-[#F5F5F5] text-[#4B5563] uppercase tracking-wider text-[11px] font-semibold">
            <tr>
              <th className="text-left px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Seleziona tutte"
                  data-testid="pa-requests-select-all"
                />
              </th>
              <th className="text-left px-4 py-3">Creata</th>
              <th className="text-left px-4 py-3">Proprietario</th>
              <th className="text-left px-4 py-3">Caso</th>
              <th className="text-left px-4 py-3">Origine</th>
              <th className="text-left px-4 py-3">Stato</th>
              <th className="text-left px-4 py-3">Consenso</th>
              <th className="text-left px-4 py-3">Assegnato a</th>
              <th className="text-left px-4 py-3">Assegnazione</th>
              <th className="text-left px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[#4B5563]">Caricamento…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[#4B5563]">Nessuna richiesta</td></tr>
            )}
            {items.map((it) => {
              const revoked = it.consent_status === "REVOKED";
              return (
              <tr
                key={it.request_id}
                className="border-t border-[#E5E5E5] hover:bg-[#FAFAFA] align-top"
                data-testid={`pa-request-row-${it.request_id}`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(it.request_id)}
                    onChange={() => toggleOne(it.request_id)}
                    aria-label={`Seleziona richiesta ${it.request_id}`}
                    data-testid={`pa-request-select-${it.request_id}`}
                  />
                </td>
                <td className="px-4 py-3 text-[#0A0A0A]/70 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                <td className="px-4 py-3 text-[#0A0A0A]">{it.owner_name || "—"}<br/>
                  <span className="text-[#0A0A0A]/60">{it.owner_email || ""}</span></td>
                <td className="px-4 py-3 text-[#0A0A0A]/60"><code className="font-mono text-[12px]">{it.case_id || "—"}</code></td>
                <td className="px-4 py-3 text-[12px]"><PaRequestOrigin it={it} /></td>
                <td className="px-4 py-3 text-[#0A0A0A]/80">{it.status || "—"}</td>
                <td className="px-4 py-3">
                  <span
                    data-testid={`pa-request-consent-${it.request_id}`}
                    className={`text-[11px] px-2 py-0.5 rounded-full ${revoked ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}
                  >{revoked ? "REVOCATO" : "ATTIVO"}</span>
                </td>
                <td className="px-4 py-3 text-[#0A0A0A]/80 text-[12px]"><PaRequestAssigned it={it} reasons={reasons} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      data-testid={`pa-assign-select-${it.request_id}`}
                      className="border border-[#D1D5DB] rounded-md px-2 py-1 text-[12px]"
                      value={chosenFor(it)}
                      onChange={(e) => setChoice({ ...choice, [it.request_id]: e.target.value })}
                      disabled={revoked}
                    >
                      <option value="">Scegli PA…</option>
                      {pas.map((p) => (
                        <option key={p.id} value={p.id}>{paLabel(p)}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      data-testid={`pa-assign-btn-${it.request_id}`}
                      onClick={() => assign(it)}
                      disabled={busy === it.request_id || revoked}
                      className="text-[12px] px-3 py-1 rounded-md bg-[#B40101] text-white disabled:opacity-50"
                    >Assegna</button>
                    <AssignReason it={it} chosen={chosenFor(it)} reasons={reasons}
                                  value={reasonSel[it.request_id] || {}}
                                  onChange={(v) => setReasonSel({ ...reasonSel, [it.request_id]: v })} />
                    {it.assigned_pa_id && it.access_active && !revoked && (
                      <button
                        type="button"
                        data-testid={`pa-revoke-btn-${it.request_id}`}
                        onClick={() => revokeAccess(it)}
                        disabled={busy === it.request_id}
                        className="text-[12px] px-3 py-1 rounded-md border border-[#D1D5DB] text-[#0A0A0A] disabled:opacity-50"
                      >Revoca accesso</button>
                    )}
                    {it.assigned_pa_id && (!it.access_active || revoked) && (
                      <span className="text-[11px] text-[#0A0A0A]/60" data-testid={`pa-access-revoked-${it.request_id}`}>
                        Accesso revocato
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setPendingDelete(it)}
                    className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-md border border-red-200 text-[#B40101] hover:bg-red-50 transition-colors"
                    data-testid={`pa-request-delete-${it.request_id}`}
                    title="Elimina richiesta"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={!!pendingDelete}
        title="Elimina richiesta di contatto PA"
        message={pendingDelete ? (
          <span data-testid="pa-request-delete-modal-body">
            Stai per eliminare la richiesta di contatto PA di{" "}
            <strong>{pendingDelete.owner_name || pendingDelete.owner_email || "—"}</strong>
            {" "}(caso <code className="font-mono text-[12px]">{pendingDelete.case_id || "—"}</code>).
            <span className="block mt-3">
              Verrà rimossa la richiesta e l'eventuale accesso del PA assegnato a questo caso.
              Il caso, la valutazione e i dati del proprietario NON vengono toccati.
            </span>
            <span className="block mt-3 font-semibold text-[#B40101]">L'operazione non è reversibile.</span>
          </span>
        ) : ""}
        confirmLabel={delBusy ? "Eliminazione…" : "Conferma elimina"}
        destructive
        onConfirm={confirmDeleteOne}
        onCancel={() => setPendingDelete(null)}
        testid="pa-request-delete-modal"
      />

      <ConfirmModal
        open={bulkConfirm}
        title={`Elimina ${selectedIds.size} richieste selezionate`}
        message={(
          <span data-testid="pa-requests-bulk-modal-body">
            Stai per eliminare <strong>{selectedIds.size}</strong> richieste di contatto PA e gli
            eventuali accessi dei PA assegnati a quei casi. I casi, le valutazioni e i dati dei
            proprietari NON vengono toccati.
            <span className="block mt-3 font-semibold text-[#B40101]">L'operazione non è reversibile.</span>
          </span>
        )}
        confirmLabel={delBusy ? "Eliminazione…" : "Conferma elimina selezionate"}
        destructive
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkConfirm(false)}
        testid="pa-requests-bulk-delete-modal"
      />
    </div>
  );
}
